import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { tyreManagementApi } from '../api/tyreManagementApi';
import { tyreQueryKeys } from '../api/queryKeys';
import useAsyncData from './useAsyncData';
import useTyreMutation from './useTyreMutation';
import { formatDate } from '../lib/utils';
import { lastThirtyDays, reportParams, requiresAsyncReport, validateReportDates, editableJobFilters } from '../lib/tyreReports';

const queuedMessage = (selection) => {
  const filters = editableJobFilters(selection);
  return `Your report is being generated: ${filters.dateFrom || 'Beginning'} – ${filters.dateTo || 'Today'}, ${filters.vehicleId ? `vehicle ${filters.vehicleId}` : 'all vehicles'}, ${filters.status === 'all' ? 'all statuses' : filters.status.replace(/_/g, ' ')}, ${filters.category === 'all' ? 'all categories' : filters.category}. You can leave this page and come back later.`;
};

export default function useReportWorkspace(active, upsertJob) {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState(() => ({ ...lastThirtyDays(), vehicleId: '', status: 'all', category: 'all' }));
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [section, setSection] = useState('tyres');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [historyTyre, setHistoryTyre] = useState('');
  const [historyVehicle, setHistoryVehicle] = useState('');
  const [queueing, setQueueing] = useState(false);
  const [queueMessage, setQueueMessage] = useState('');
  const [lastQueuedKey, setLastQueuedKey] = useState('');
  const [loadingResultId, setLoadingResultId] = useState(null);
  const [viewedReport, setViewedReport] = useState(null);
  const [page, setPage] = useState(1);
  const [operationError, setOperationError] = useState('');
  const liveSelection = useRef(null);
  const resultRequest = useRef(0);
  const cancelResultOpening = useCallback(() => {
    resultRequest.current += 1;
    setLoadingResultId(null);
  }, []);
  useEffect(() => () => { resultRequest.current += 1; }, []);
  const dateError = validateReportDates(filters);
  const key = JSON.stringify(reportParams(appliedFilters));
  const pendingKey = JSON.stringify(reportParams(filters));
  useEffect(() => { setPage(1); }, [key, section, historyTyre, historyVehicle, viewedReport]);
  const filtersChanged = pendingKey !== key;
  const asyncRequired = requiresAsyncReport(filters);
  // Associate each response with the submitted filters so stale requests cannot replace it.
  const reportState = useAsyncData(
    tyreQueryKeys.report(JSON.parse(key)),
    async () => ({ key, report: await tyreManagementApi.getReport(JSON.parse(key)) }),
    active && !viewedReport
  );
  const createJobMutation = useTyreMutation(tyreManagementApi.createReportJob, { invalidate: false });
  const exportMutation = useTyreMutation(({ format, params, job }) => (job
    ? tyreManagementApi.exportReportJob(job.id, format, section)
    : tyreManagementApi.exportReport(format, params)), { invalidate: false });
  useEffect(() => {
    const response = reportState.data?.report;
    if (response?.id && response?.status && !response?.summary) {
      upsertJob(response);
      setLastQueuedKey(reportState.data.key);
      setQueueMessage(queuedMessage(JSON.parse(reportState.data.key)));
    }
  }, [reportState.data, upsertJob]);
  const syncReport = !reportState.loading && !reportState.error && reportState.data?.key === key
    && !reportState.data?.report?.status ? reportState.data.report : null;
  const report = viewedReport?.report || syncReport;
  const vehicleOptions = report?.vehicleOptions || reportState.data?.report?.vehicleOptions || [];
  const updateFilters = (changes) => {
    setOperationError(''); setExportError(''); setQueueMessage(''); setLastQueuedKey(''); setFilters((current) => ({ ...current, ...changes }));
  };
  const applyFilters = async (event) => {
    event.preventDefault();
    if (dateError || !filtersChanged || queueing || lastQueuedKey === pendingKey) return;
    setHistoryTyre('');
    setHistoryVehicle('');
    setOperationError('');
    if (!asyncRequired) {
      resultRequest.current += 1;
      setLoadingResultId(null);
      liveSelection.current = null;
      setViewedReport(null);
      setAppliedFilters({ ...filters });
      return;
    }
    setQueueing(true);
    setOperationError('');
    try {
      const job = await createJobMutation.mutateAsync(reportParams(filters));
      upsertJob(job);
      setLastQueuedKey(pendingKey);
      setQueueMessage(queuedMessage(filters));
    } catch (error) {
      setOperationError(error.message || 'The report could not be queued. Try again.');
    } finally { setQueueing(false); }
  };
  const exportReport = async (format) => {
    setExporting(true);
    setExportError('');
    try {
      const response = await exportMutation.mutateAsync({
        format,
        job: viewedReport?.job,
        params: { ...JSON.parse(key), section },
      });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `tyre-${section}-${appliedFilters.dateFrom || 'all'}-${appliedFilters.dateTo || 'time'}.${format}`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      setExportError('The report could not be exported. Try again.');
    } finally { setExporting(false); }
  };
  const adoptReport = ({ report: nextReport, job = null, filters: nextFilters, vehicleId = '' }) => {
    resultRequest.current += 1;
    setLoadingResultId(null);
    setOperationError('');
    setExportError('');
    if (!viewedReport?.job && job) liveSelection.current = { filters, appliedFilters };
    if (!job) liveSelection.current = null;
    setViewedReport({ job, report: nextReport });
    setFilters({ ...nextFilters });
    setAppliedFilters({ ...nextFilters });
    setHistoryTyre('');
    setHistoryVehicle(String(vehicleId));
    setSection(vehicleId ? 'history' : 'tyres');
    setLastQueuedKey('');
    setQueueMessage('');
  };
  const viewGeneratedReport = async (job, preserveView = false) => {
    const request = ++resultRequest.current;
    setLoadingResultId(job.id);
    setOperationError('');
    try {
      const result = await queryClient.fetchQuery({
        queryKey: tyreQueryKeys.reportJobResult(job.id),
        queryFn: () => tyreManagementApi.getReportJobResult(job.id),
        staleTime: 0,
      });
      if (request !== resultRequest.current) return false;
      adoptReport({ job, report: result?.report || result, filters: editableJobFilters(job.filters) });
      if (preserveView) {
        setFilters(filters);
        setSection(section);
        setHistoryTyre(historyTyre);
        setHistoryVehicle(historyVehicle);
      }
      return true;
    } catch (error) {
      if (request === resultRequest.current) setOperationError(error.message || 'The generated report could not be opened.');
      return false;
    } finally {
      if (request === resultRequest.current) setLoadingResultId(null);
    }
  };
  const returnToLive = () => {
    resultRequest.current += 1;
    setLoadingResultId(null);
    const previous = liveSelection.current || { filters: { ...lastThirtyDays(), vehicleId: '', status: 'all', category: 'all' } };
    setFilters(previous.filters);
    setAppliedFilters(previous.appliedFilters || previous.filters);
    setViewedReport(null);
    liveSelection.current = null;
    setHistoryTyre('');
    setHistoryVehicle('');
    setQueueMessage('');
    setLastQueuedKey('');
    setOperationError('');
    setExportError('');
  };
  const refresh = () => {
    if (viewedReport?.job) return viewGeneratedReport(viewedReport.job, true);
    if (viewedReport) { setViewedReport(null); return undefined; }
    return reportState.reload();
  };
  const showVehicleHistory = (vehicleId) => {
    setHistoryTyre('');
    setHistoryVehicle(String(vehicleId));
    setSection('history');
  };
  const showTyreHistory = (tyreId) => { setHistoryTyre(tyreId); setHistoryVehicle(''); setSection('history'); };
  const period = appliedFilters.dateFrom || appliedFilters.dateTo ? `${appliedFilters.dateFrom ? formatDate(`${appliedFilters.dateFrom}T00:00:00`) : 'Beginning'} – ${appliedFilters.dateTo ? formatDate(`${appliedFilters.dateTo}T00:00:00`) : 'Today'}` : 'All time';
  const activity = report?.activitySummary || {};
  const summary = report?.summary || {};

  return { filters, appliedFilters, section, setSection, exporting, exportError, historyTyre, setHistoryTyre, historyVehicle, setHistoryVehicle,
    queueing, queueMessage, lastQueuedKey, loadingResultId, viewedReport, dateError, key, pendingKey, filtersChanged, asyncRequired,
    reportState, report, vehicleOptions, updateFilters, applyFilters, exportReport, viewGeneratedReport, adoptReport, returnToLive, refresh,
    showVehicleHistory, showTyreHistory, period, activity, summary, operationError, page, setPage, cancelResultOpening };
}
